import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { MonthNavigator } from '../../components/budget/MonthNavigator';
import { Panel } from '../../components/common/Panel';
import { FixedCostsPanel } from '../../components/plan/FixedCostsPanel';
import { FlowCard } from '../../components/plan/FlowCard';
import { IncomePanel } from '../../components/plan/IncomePanel';
import { LimitsPanel } from '../../components/plan/LimitsPanel';
import { PanelSkeleton } from '../../components/plan/PanelSkeleton';
import { SavingsPanel } from '../../components/plan/SavingsPanel';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';
import { useViewPeriod } from '../../lib/useViewPeriod';

const searchSchema = z.object({
  year: z.coerce.number().int().optional().catch(undefined),
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
});

export const Route = createFileRoute('/plan/')({
  validateSearch: searchSchema,
  component: PlanPage,
});

/** "How does our month work?": income, fixed costs, what was saved, limits. */
function PlanPage() {
  usePageTitle('Plan');
  const search = useSearch({ from: '/plan/' });
  const navigate = useNavigate();
  const { year, month, ready, max } = useViewPeriod(search);

  const summaryQuery = trpc.period.summary.useQuery(
    { year, month },
    { enabled: ready, retry: false },
  );
  const limitsQuery = trpc.budgetGroup.list.useQuery();
  const incomeQuery = trpc.income.list.useQuery();
  const summary = summaryQuery.data;

  const setPeriod = (nextYear: number, nextMonth: number) => {
    void navigate({
      from: '/plan/',
      search: (prev) => ({ ...prev, year: nextYear, month: nextMonth }),
      replace: true,
    });
  };

  const period = summary?.period;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <MonthNavigator
        year={year}
        month={month}
        onChange={setPeriod}
        max={max}
        range={period}
        meta={period?.isCurrent ? `dag ${period.daysElapsed} av ${period.daysTotal}` : undefined}
      />

      {summaryQuery.error ? (
        <ErrorPanel message={`Kunde inte ladda perioden: ${summaryQuery.error.message}`} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
          <div className="space-y-4">
            {summary ? (
              <FlowCard summary={summary} />
            ) : (
              <PanelSkeleton title="Så går månaden ihop" rows={4} />
            )}
            {summary ? (
              <FixedCostsPanel summary={summary} />
            ) : (
              <PanelSkeleton title="Fasta kostnader" rows={5} />
            )}
          </div>
          <div className="space-y-4">
            {limitsQuery.error ? (
              <ErrorPanel title="Gränser" message={limitsQuery.error.message} />
            ) : summary && limitsQuery.data ? (
              <LimitsPanel limits={limitsQuery.data} summary={summary} />
            ) : (
              <PanelSkeleton title="Gränser" rows={2} />
            )}
            {summary ? (
              <SavingsPanel summary={summary} />
            ) : (
              <PanelSkeleton title="Sparat i perioden" rows={1} />
            )}
            {incomeQuery.error ? (
              <ErrorPanel title="Inkomster" message={incomeQuery.error.message} />
            ) : incomeQuery.data ? (
              <IncomePanel sources={incomeQuery.data} />
            ) : (
              <PanelSkeleton title="Inkomster" rows={1} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorPanel({ title, message }: { title?: string; message: string }) {
  return (
    <Panel title={title}>
      <p role="alert" className="text-sm text-negative">
        {message}
      </p>
    </Panel>
  );
}
